/**
 * PKCE (RFC 7636) + anti-CSRF nonce helpers for the OAuth login flow.
 *
 * Sign-in mints a pair and stashes it before redirecting; the callback reads it
 * back to verify the echoed `state.csrf` and send the verifier on the exchange.
 *
 * sessionStorage is deliberate — being per-tab, concurrent logins in separate
 * tabs cannot clobber each other.
 */
import { parseStateObject } from './oauthState'

const CHALLENGE_KEY = 'oauth_challenge'
const REAUTHORIZE_ATTEMPTED_KEY = 'oauth_reauthorize_attempted'

/**
 * Per [RFC 7636](https://datatracker.ietf.org/doc/html/rfc7636): 32 random
 * bytes, base64url-encoded unpadded -> a 43-char verifier, inside the required
 * 43-128 range. Verify current best practices before changing this.
 *
 * svc/account also validates the unreserved charset `[A-Za-z0-9-._~]`, so the
 * padding `btoa` adds would be rejected.
 */
const RANDOM_BYTES = 32

export type OAuthChallenge = {
  /** Random nonce echoed back inside the OAuth `state` and verified on return. */
  csrf: string
  /** Secret; must never leave sessionStorage except on the token exchange. */
  codeVerifier: string
  /** `base64url(SHA-256(codeVerifier))` — safe to put in the authorize URL. */
  codeChallenge: string
}

const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const randomBase64Url = (): string =>
  base64UrlEncode(crypto.getRandomValues(new Uint8Array(RANDOM_BYTES)))

/**
 * Authorize-URL params for the challenge; the verifier itself must never
 * appear in a URL. `code_challenge_method` is always explicit — Salesforce
 * treats an absent method as `plain`, which would fail the exchange.
 */
export const challengeParams = (challenge: OAuthChallenge | null) =>
  challenge
    ? {
        code_challenge: challenge.codeChallenge,
        code_challenge_method: 'S256',
      }
    : {}

/** Adds the anti-CSRF nonce to the OAuth `state`, leaving existing keys alone. */
export const withCsrf = <T extends object>(
  state: T,
  challenge: OAuthChallenge | null
) => (challenge ? { ...state, csrf: challenge.csrf } : state)

/**
 * WebCrypto needs a secure context. If it is missing we skip PKCE entirely
 * rather than sending a challenge we could never satisfy.
 */
export const isWebCryptoAvailable = (): boolean =>
  typeof crypto !== 'undefined' &&
  typeof crypto.getRandomValues === 'function' &&
  // `crypto.subtle` is typed as always present but is genuinely undefined outside
  // a secure context, so this stays a runtime check.
  typeof (crypto.subtle as SubtleCrypto | undefined)?.digest === 'function'

/** The S256 transformation: `base64url(SHA-256(verifier))`. */
export const deriveCodeChallenge = async (
  codeVerifier: string
): Promise<string> => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier)
  )
  return base64UrlEncode(new Uint8Array(digest))
}

export const mintOAuthChallenge = async (): Promise<OAuthChallenge> => {
  const codeVerifier = randomBase64Url()
  return {
    csrf: randomBase64Url(),
    codeVerifier,
    codeChallenge: await deriveCodeChallenge(codeVerifier),
  }
}

/**
 * Returns false when the pair could not be persisted. Callers MUST NOT put a
 * `code_challenge` on the authorize request in that case: the identity provider
 * would bind the code to a challenge whose verifier we can no longer produce,
 * making the login unrecoverable.
 */
export const storeOAuthChallenge = (challenge: OAuthChallenge): boolean => {
  try {
    sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(challenge))
    return true
  } catch {
    return false
  }
}

export const readOAuthChallenge = (): OAuthChallenge | null => {
  try {
    const raw = sessionStorage.getItem(CHALLENGE_KEY)
    if (!raw) {
      return null
    }
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    const { csrf, codeVerifier, codeChallenge } =
      parsed as Partial<OAuthChallenge>
    if (!csrf || !codeVerifier || !codeChallenge) {
      return null
    }
    return { csrf, codeVerifier, codeChallenge }
  } catch {
    return null
  }
}

/**
 * Pulls the nonce out of the `state` echoed back by the identity provider.
 * Untrusted input — tampering is exactly what we are checking for.
 */
export const readEchoedCsrf = (state: unknown): string | null => {
  const csrf = parseStateObject(state)?.csrf
  return typeof csrf === 'string' && csrf ? csrf : null
}

export const clearOAuthChallenge = (): void => {
  try {
    sessionStorage.removeItem(CHALLENGE_KEY)
  } catch {
    // Nothing we can do, and nothing depends on the removal succeeding.
  }
}

/**
 * One-shot guard for the callback's silent re-authorize; lives in
 * sessionStorage because it must survive a full page navigation.
 */
export const hasAttemptedReauthorize = (): boolean => {
  try {
    return sessionStorage.getItem(REAUTHORIZE_ATTEMPTED_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Returns false when the guard could not be persisted — the caller must then
 * NOT re-authorize, otherwise a browser with storage disabled would bounce
 * between the callback and the identity provider forever.
 */
export const markReauthorizeAttempted = (): boolean => {
  try {
    sessionStorage.setItem(REAUTHORIZE_ATTEMPTED_KEY, 'true')
    return sessionStorage.getItem(REAUTHORIZE_ATTEMPTED_KEY) === 'true'
  } catch {
    return false
  }
}

export const clearReauthorizeAttempted = (): void => {
  try {
    sessionStorage.removeItem(REAUTHORIZE_ATTEMPTED_KEY)
  } catch {
    // See clearOAuthChallenge.
  }
}
